import { getWidthSrcSet } from '@uefadigital/panenka';
import useResizeObserver from 'beautiful-react-hooks/useResizeObserver';
import clsx from 'clsx';
import type { SyntheticEvent } from 'react';
import { memo, useRef, useState, useMemo, useCallback } from 'react';

import { useImgVisible } from './hooks/useImgVisible';

const ImageComponent = ({
  src,
  className = '',
  fallback = `${window.imgBaseUrl}/imgml/void.png`,
  title = '',
  alt = title,
  width,
  height,
  slot,
  onLoad,
  onError,
}: {
  src: string;
  className?: string;
  fallback?: string;
  title?: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  slot?: string;
  onLoad?: (ev: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (ev: SyntheticEvent<HTMLImageElement>) => void;
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isError, setIsError] = useState(false);
  const isVisible = useImgVisible(imgRef);
  const DOMRect = useResizeObserver(imgRef);

  const setImgError = (e: SyntheticEvent<HTMLImageElement> | Event | string) => {
    onError && typeof onError === 'function' && onError(e as SyntheticEvent<HTMLImageElement>);
    isVisible && setIsError(true);
  };

  const onImgLoad = (e: SyntheticEvent<HTMLImageElement> | Event) => {
    onLoad && typeof onLoad === 'function' && onLoad(e as SyntheticEvent<HTMLImageElement>);
  };

  const imgSize = useMemo(() => (DOMRect?.width ? Math.ceil(DOMRect.width / 10) * 10 : null), [DOMRect?.width]);

  const getImgSrcSet = useCallback(
    (getFirst) => {
      let srcSet = getWidthSrcSet(fallback, getFirst);
      if (src && imgSize && isVisible && !isError) {
        srcSet = getWidthSrcSet(src, getFirst);
      }

      return srcSet;
    },
    [fallback, imgSize, isError, isVisible, src],
  );

  const classNames = clsx({
    lazyfallback: isError && !fallback,
    [className]: !!className,
  });

  return (
    <img
      ref={imgRef}
      slot={slot}
      style={{
        minWidth: isError ? 'none' : 1,
        minHeight: isError ? 'none' : 1,
      }}
      className={classNames}
      onLoad={onImgLoad}
      onError={setImgError}
      loading='lazy'
      sizes={`${imgSize ?? 0}px`}
      src={getImgSrcSet(true)}
      srcSet={getImgSrcSet(false)}
      title={title}
      alt={alt}
      width={width}
      height={height}
    />
  );
};

export default memo(ImageComponent);
